import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CasosListSkeleton } from '@/components/skeletons';
import {
  applyClientFilters,
  CasosToolbar,
  hasAnyFilter,
  type CasosFilterState,
} from '@/components/Casos/CasosToolbar';
import { ptBR } from '@/i18n/pt-BR';
import { CaseError, type CaseRecordData, listCases } from '@/lib/cases';
import { CasosExportButton } from '@/components/Casos/CasosExportButton';
import { getOfficeKpis, type OfficeKpisData } from '@/lib/office_kpis';
import {
  Blueprint,
  Button,
  KpiCard,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { CasosTableRow } from '@/components/Casos/CasosTableRow';
import { usePrioridadeCaso } from '@/components/Casos/usePrioridadeCaso';
import { useBaixarRelatorio } from '@/components/Casos/useBaixarRelatorio';
import { CasosPagination, clampPage, type PageSize } from '@/components/Casos/CasosPagination';
import {
  filtersFromSearchParams,
  pageFromSearchParams,
  searchParamsFromFilters,
  sizeFromSearchParams,
} from '@/components/Casos/url_state';
import { digitsOnly } from '@/lib/masks';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { EsteiraPortal } from '@/components/Casos/EsteiraPortal';

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

const DEADLINE_NEAR_DAYS = 7;

interface CaseKpis {
  casosNoMes: number;
  ativos: number;
  aguardamPrazo: number;
  conflito: number;
}

function computeLocalKpis(cases: readonly CaseRecordData[], now: Date): CaseKpis {
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  const nearMs = DEADLINE_NEAR_DAYS * 24 * 60 * 60 * 1000;
  return cases.reduce<CaseKpis>(
    (acc, c) => {
      const created = new Date(c.created_at);
      if (created.getUTCFullYear() === year && created.getUTCMonth() === month) {
        acc.casosNoMes += 1;
      }
      if (c.status === 'analysing') acc.ativos += 1;
      if (c.status === 'conflict') acc.conflito += 1;
      if (c.deadline !== null) {
        const deadline = new Date(`${c.deadline}T00:00:00Z`).getTime();
        const diff = deadline - now.getTime();
        if (diff >= 0 && diff <= nearMs) acc.aguardamPrazo += 1;
      }
      return acc;
    },
    { casosNoMes: 0, ativos: 0, aguardamPrazo: 0, conflito: 0 },
  );
}

function fillSubtitle(template: string, kpis: CaseKpis): string {
  return template
    .replace('{{ativos}}', String(kpis.ativos))
    .replace('{{aguardamPrazo}}', String(kpis.aguardamPrazo))
    .replace('{{conflito}}', String(kpis.conflito));
}

function formatHours(hours: number | null | undefined): string {
  if (hours == null) return ptBR.casos.placeholderEmDash;
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  return `${hours.toFixed(1)} h`;
}

export function CasosPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { role, isPlatform } = useCurrentRole();
  const canReview = role === 'owner' || role === 'revisor';
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const page = pageFromSearchParams(searchParams);
  const pageSize = sizeFromSearchParams(searchParams);

  const [cases, setCases] = useState<CaseRecordData[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [officeKpis, setOfficeKpis] = useState<OfficeKpisData | null>(null);
  const { emVoo: prioridadeEmVoo, alternar: togglePrioridade } = usePrioridadeCaso(
    apiClient,
    setCases,
    useCallback(() => setErrorMessage(ptBR.casos.table.prioridadeErro), []),
  );
  const { emVoo: relatorioEmVoo, baixar: baixarRelatorio } = useBaixarRelatorio(
    apiClient,
    useCallback(() => setErrorMessage(ptBR.preAnalise.errorRelatorioDownload), []),
  );

  const debouncedCpf = useDebouncedValue(filters.cpf, SEARCH_DEBOUNCE_MS);
  const debouncedContrato = useDebouncedValue(filters.contrato, SEARCH_DEBOUNCE_MS);
  const cpfDigits = digitsOnly(debouncedCpf);

  useEffect(() => {
    let cancelled = false;
    setErrorMessage(null);
    listCases(apiClient, { cpf: cpfDigits, contrato: debouncedContrato })
      .then((items) => {
        if (!cancelled) setCases(items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof CaseError && err.reason === 'network') {
          setErrorMessage(ptBR.errors.networkOffline);
        } else {
          setErrorMessage(ptBR.casos.errorLoad);
        }
        setCases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, cpfDigits, debouncedContrato]);

  useEffect(() => {
    let cancelled = false;
    getOfficeKpis(apiClient)
      .then((data) => {
        if (!cancelled) setOfficeKpis(data);
      })
      .catch(() => {
        /* KPI load failure is non-blocking; dashboard remains usable */
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const patchFilters = useCallback(
    (next: Partial<CasosFilterState>) => {
      const merged: CasosFilterState = { ...filters, ...next };
      setSearchParams(searchParamsFromFilters(merged, 1, pageSize), { replace: true });
    },
    [filters, pageSize, setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback(
    (next: number) => {
      setSearchParams(searchParamsFromFilters(filters, next, pageSize), { replace: true });
    },
    [filters, pageSize, setSearchParams],
  );

  const setPageSize = useCallback(
    (next: PageSize) => {
      setSearchParams(searchParamsFromFilters(filters, 1, next), { replace: true });
    },
    [filters, setSearchParams],
  );

  const filteredCases = useMemo(
    () => (cases === null ? null : applyClientFilters(cases, filters)),
    [cases, filters],
  );

  const total = filteredCases?.length ?? 0;
  const safePage = clampPage(page, total, pageSize);
  const pagedCases = useMemo(() => {
    if (filteredCases === null) return null;
    const startIdx = (safePage - 1) * pageSize;
    return filteredCases.slice(startIdx, startIdx + pageSize);
  }, [filteredCases, safePage, pageSize]);

  const localKpis = useMemo<CaseKpis>(() => {
    if (cases === null) return { casosNoMes: 0, ativos: 0, aguardamPrazo: 0, conflito: 0 };
    return computeLocalKpis(cases, new Date());
  }, [cases]);

  const filtersActive = hasAnyFilter(filters);

  return (
    <section data-testid="casos-page" aria-labelledby="casos-title" className="mx-auto max-w-7xl">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
            {ptBR.casos.eyebrow}
          </p>
          <h1 id="casos-title" className="font-display text-[38px] leading-none text-fg">
            {ptBR.casos.pageTitle}
          </h1>
          <p
            className="mt-1 font-mono text-caption uppercase tracking-[0.02em] text-fg-dim"
            data-testid="casos-subtitle"
          >
            {fillSubtitle(ptBR.casos.subtitleTemplate, localKpis)}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2.5">
          <CasosExportButton cpf={cpfDigits} contrato={debouncedContrato} />
          <Button
            type="button"
            data-testid="novo-caso-btn"
            className="h-[38px]"
            onClick={() => navigate('/casos/novo')}
          >
            {ptBR.casos.novoCaso}
          </Button>
        </div>
      </header>

      <div className="mt-[26px] grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="casos-kpis">
        <KpiCard
          title={ptBR.casos.kpiCasosNoMes}
          value={String(localKpis.casosNoMes)}
          data-testid="kpi-casos-no-mes"
        />
        <KpiCard
          title={ptBR.casos.kpiTempoMedio}
          value={formatHours(officeKpis?.tempo_medio_analise_horas)}
          data-testid="kpi-tempo-medio"
        />
        <KpiCard
          title={ptBR.casos.kpiLrAutoria}
          value={
            officeKpis != null ? String(officeKpis.lr_autoria_count) : ptBR.casos.placeholderEmDash
          }
          data-testid="kpi-lr-autoria"
        />
        <KpiCard
          title={ptBR.casos.kpiLrHeteroautoria}
          value={
            officeKpis != null
              ? String(officeKpis.lr_heteroautoria_count)
              : ptBR.casos.placeholderEmDash
          }
          data-testid="kpi-lr-heteroautoria"
        />
      </div>

      <EsteiraPortal visible={isPlatform} />

      <div className="mt-4">
        <CasosToolbar
          filters={filters}
          onPatch={patchFilters}
          onClearAll={clearAll}
          canReview={canReview}
        />
      </div>

      {errorMessage !== null ? (
        <p
          role="alert"
          data-testid="casos-error"
          className="mt-4 border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
        >
          {errorMessage}
        </p>
      ) : null}

      {pagedCases === null || filteredCases === null ? (
        <div data-testid="casos-loading" className="mt-6">
          <span className="sr-only">{ptBR.casos.loading}</span>
          <CasosListSkeleton />
        </div>
      ) : filteredCases.length === 0 ? (
        filtersActive ? (
          <div
            data-testid="casos-empty-filtered"
            className="mt-6 border border-dashed border-border px-6 py-10 text-center"
          >
            <p className="text-body-lg text-fg">{ptBR.casos.emptyFiltered.title}</p>
            <p className="mt-2 text-body-md text-fg-muted">
              {ptBR.casos.emptyFiltered.description}
            </p>
            <Button
              type="button"
              variant="secondary"
              data-testid="casos-empty-filtered-clear"
              onClick={clearAll}
              className="mt-4"
            >
              {ptBR.casos.emptyFiltered.cta}
            </Button>
          </div>
        ) : (
          <div
            data-testid="casos-empty"
            className="mt-6 border border-dashed border-border px-6 py-10 text-center"
          >
            <p className="text-body-lg text-fg">{ptBR.casos.emptyTitle}</p>
            <p className="mt-2 text-body-md text-fg-muted">{ptBR.casos.emptyDescription}</p>
          </div>
        )
      ) : (
        <Blueprint className="mt-6 px-sc-6 pb-3.5 pt-2">
          {/* O recorte horizontal fica num filho, não no card: a régua de
              rolagem da tela é o `#main-content`, e uma tabela larga vazando de
              um `overflow-x: auto` no card empurraria a página inteira. */}
          <div className="overflow-x-auto [contain:layout]">
            <Table className="min-w-[56rem] text-left">
              <TableHeader>
                <TableRow>
                  <TableHead>{ptBR.casos.tableColumnNumero}</TableHead>
                  <TableHead>{ptBR.casos.tableColumnCaso}</TableHead>
                  <TableHead>{ptBR.casos.tableColumnEspecime}</TableHead>
                  <TableHead>{ptBR.casos.tableColumnLr}</TableHead>
                  <TableHead>{ptBR.casos.tableColumnStatus}</TableHead>
                  <TableHead>{ptBR.casos.tableColumnPrazo}</TableHead>
                  <TableHead data-testid="th-col-received-at">
                    {ptBR.casos.tableColumnAssinatura}
                  </TableHead>
                  <TableHead data-testid="th-col-created-at">
                    {ptBR.casos.tableColumnCadastro}
                  </TableHead>
                  <TableHead className="text-right">{ptBR.casos.table.columnActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedCases.map((c) => {
                  return (
                    <CasosTableRow
                      key={c.id}
                      caseRecord={c}
                      alterandoPrioridade={prioridadeEmVoo === c.id}
                      baixandoRelatorio={relatorioEmVoo === c.id}
                      // E78.F1: quem decide a tela é o resolvedor `/casos/:id`,
                      // que conhece modalidade e estado. A lista escolhia a rota
                      // por `status === 'draft'` e mandava todo caso eletrônico
                      // não-rascunho para a mesa grafotécnica, que o recusa.
                      onOpen={() => navigate(`/casos/${c.id}`)}
                      onPreAnalise={() => navigate(`/casos/${c.id}/pre-analise`)}
                      onTogglePrioridade={() => void togglePrioridade(c)}
                      onBaixarRelatorio={() => void baixarRelatorio(c.id)}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <CasosPagination
            total={total}
            page={safePage}
            size={pageSize}
            onPageChange={setPage}
            onSizeChange={setPageSize}
          />
        </Blueprint>
      )}
    </section>
  );
}
